import {
  NarrativeCardSynthetic,
  NarrativeCardPopulated,
} from '@/components/NarrativeCard';

import { FragmentOf, gql, readFragment } from '@/utilities/gql';
import { NARRATIVE_CARD_FRAGMENT } from '@/components/NarrativeCard/narrativeCardFragment';
import { AriaRole, useCallback } from 'react';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { useTracking } from 'react-tracking';
import { narrativeFvModalReturnPath } from '../../utils';
import { usePathname } from '@/hooks/location/usePathname';
import { NarrativeSetFairValue } from '@/pages/Narrative/components/NarrativeSetFairValue';
import { NarrativeInvalidationBoundary } from '@/pages/Narrative/components/NarrativeInvalidationBoundary';

export const NARRATIVE_CARD_COMPANY_FRAGMENT = gql(`
  fragment NarrativeCardOptionCompanyFields on Company @_unmask {
    id
    canonicalURL
    data {
      currencyInfo
    }
    analysisValue {
      priceTargetAnalystCount
    }
  }
`);

export const NARRATIVE_CARD_OPTION_FRAGMENT = gql(
  `
  fragment NarrativeCardOptionValuationOptionFields on ValuationOption @_unmask {
    type
    fairValue
    intrinsicDiscount

    narrative {
      id
      title
      isPrimary
      ...NarrativeCardFields
    }
  }
`,
  [NARRATIVE_CARD_FRAGMENT]
);

type Props = {
  valuationOption: FragmentOf<typeof NARRATIVE_CARD_OPTION_FRAGMENT>;
  company: FragmentOf<typeof NARRATIVE_CARD_COMPANY_FRAGMENT>;
  role?: AriaRole;
  onError: () => void;
};

const isSyntheticNarrative = (type: string): type is 'DCF' | 'ANALYSTS' =>
  ['DCF', 'ANALYSTS'].includes(type);

export const NarrativeCardOption = ({
  valuationOption,
  company,
  role,
  onError,
}: Props) => {
  const { trackEvent } = useTracking();
  const pathname = usePathname();

  const valuationOptionUnmasked = readFragment(
    NARRATIVE_CARD_OPTION_FRAGMENT,
    valuationOption
  );
  const { narrative, type, fairValue, intrinsicDiscount } =
    valuationOptionUnmasked;

  const handleCardClick = useCallback(() => {
    trackEvent({
      action: 'click',
      modifier: 'view',
      narrativeId: narrative?.id,
      companyId: company?.id,
      valuationType: type,
    });
  }, [trackEvent, narrative?.id, company?.id, type]);

  if (!narrative) {
    console.error('Narrative is missing from valuation option');
    return null;
  }

  const { id, isPrimary } = narrative ?? {};

  return (
    <div role={role}>
      <NarrativeInvalidationBoundary
        onSetPrimary={({ invalidatePriority }) =>
          invalidatePriority(['narrative-valuation-options'])
        }
      >
        {isSyntheticNarrative(type) ? (
          <NarrativeCardSynthetic
            className="rounded-none min-[375px]:rounded-x1_5"
            title={narrative.title}
            fairValue={fairValue}
            intrinsicDiscount={intrinsicDiscount}
            currencyIso={
              getTradingItemCurrencyISO(company.data.currencyInfo) ?? 'XXX'
            }
            priceTargetAnalystCount={
              company.analysisValue?.priceTargetAnalystCount
            }
            type={type}
            linkTo={`${company.canonicalURL}/valuation`}
            narrativeId={id}
            onClick={handleCardClick}
          >
            {company.id && (
              <NarrativeSetFairValue
                companyId={company.id}
                narrativeId={id}
                isPrimary={isPrimary}
                onError={onError}
                valuationType={type}
              />
            )}
          </NarrativeCardSynthetic>
        ) : (
          <NarrativeCardPopulated
            narrative={narrative}
            className="rounded-none min-[375px]:rounded-x1_5"
            onClick={handleCardClick}
            returnPath={narrativeFvModalReturnPath(pathname, company?.id)}
          />
        )}
      </NarrativeInvalidationBoundary>
    </div>
  );
};
